import find from "lodash.find"
import last from "lodash.last"

import { useAssertValidRootQuery } from "graphql/smart-queries/user"
import {
  userShouldGoToAcceptLegals,
  userShouldGoToCompleteQuiz,
  userShouldGoToPhotoUpload,
  userShouldGoToApproveTreatment,
  userShouldGoToCheckout,
  userShouldCompleteAnnualRenewal,
  renewalConsultationPending
} from "shared/wayfinder"
import {
  JourneyKindEnum,
  StatusEnum,
  ConsultationMessagePrescriptionStatusEnum,
  SubscriptionStatusEnum,
  NextActionEnum
} from "typings/types"
import { maybeGetActiveJourney } from "shared/user/helpers"

export enum DashboardNotificationTypeEnum {
  NotAMatch = "NotAMatch",
  ProceedToCheckout = "ProceedToCheckout",
  AcceptLegals = "AcceptLegals",
  FinishConsultation = "FinishConsultation",
  TreatmentChanged = "TreatmentChanged",
  NewMessage = "NewMessage",
  UpdateTreatmentBanner = "UpdateTreatmentBanner",
  ProceedToAnnualRenewal = "ProceedToAnnual",
  RenewalConsultationPending = "RenewalConsultationPending"
}

export const useDashboardNotificationType = (): Maybe<DashboardNotificationTypeEnum> => {
  const { user } = useAssertValidRootQuery()
  const { consultation, subscriptions, hasDoctorTreatmentChange } = user

  const activeJourney = maybeGetActiveJourney(user)
  const activeSubscription = find(subscriptions, { status: SubscriptionStatusEnum.Active })
  const subscription = activeSubscription || last(subscriptions)

  const notAMatch =
    consultation?.doctorMessageDisplayed?.prescriptionStatus === ConsultationMessagePrescriptionStatusEnum.NotAMatch
  const isCanceled = subscription?.status === SubscriptionStatusEnum.Canceled
  const hasUnreadMessages = consultation && consultation.unreadDoctorMessagesCount > 0
  const consultationCompleted = consultation?.status === StatusEnum.Completed
  const isSubscribedHasUnapprovedPrescription =
    activeSubscription &&
    (!activeJourney || activeJourney?.kind !== JourneyKindEnum.CovePurchase) &&
    hasDoctorTreatmentChange

  /* We display "treatment plan changed" notification when:
   *   - User is on his initial journey and needs to approve the treatment from doc
   *   - User is already subscribed and doc is adjusting the plan.
   *     At this point initial journey is in "completed" state, UpdateTreatment journey is created
   *   - User subscription is canceled
   */
  const showTreatmentPlanChanged =
    (consultationCompleted && userShouldGoToApproveTreatment(activeJourney)) ||
    isSubscribedHasUnapprovedPrescription ||
    (!activeSubscription && !activeJourney && isCanceled)

  const completedOnboardingJourney = (consultationCompleted || user.eCommerceUser) && !activeJourney
  const hasActiveUtpJourneyBeforeCheckout =
    activeJourney?.kind === JourneyKindEnum.CoveUpdateTreatment &&
    activeJourney.nextActions.includes(NextActionEnum.Checkout)
  /* We display "update treatment banner" when:
   * - User has completed the onboarding journey (CYOM or LTDD)
   * - User has an active CoveUpdateTreatment journey
   */
  const showSupplementsBanner = completedOnboardingJourney || hasActiveUtpJourneyBeforeCheckout

  let notificationType: DashboardNotificationTypeEnum | undefined

  if (notAMatch) {
    notificationType = DashboardNotificationTypeEnum.NotAMatch
  } else if (userShouldGoToCheckout(activeJourney)) {
    notificationType = DashboardNotificationTypeEnum.ProceedToCheckout
  } else if (userShouldGoToAcceptLegals(activeJourney)) {
    notificationType = DashboardNotificationTypeEnum.AcceptLegals
  } else if (userShouldGoToCompleteQuiz(activeJourney) || userShouldGoToPhotoUpload(activeJourney)) {
    notificationType = DashboardNotificationTypeEnum.FinishConsultation
  } else if (userShouldCompleteAnnualRenewal(activeJourney)) {
    notificationType = DashboardNotificationTypeEnum.ProceedToAnnualRenewal
  } else if (renewalConsultationPending(activeJourney)) {
    notificationType = DashboardNotificationTypeEnum.RenewalConsultationPending
  } else if (showTreatmentPlanChanged) {
    notificationType = DashboardNotificationTypeEnum.TreatmentChanged
  } else if (hasUnreadMessages) {
    notificationType = DashboardNotificationTypeEnum.NewMessage
  } else if (showSupplementsBanner) {
    notificationType = DashboardNotificationTypeEnum.UpdateTreatmentBanner
  }

  return notificationType
}
